import { buildQueryString } from 'lib/utils/utils.js';
import { httpService } from './http.service.js';

const endpoint = 'ai-product';

export const aiProductService = {
  archiveProduct,
  getById,
  getByIdToArr,
  getAll,
  getByOrgId,
  getByOrgIdWithSubs,
  getSummaryWithSubs,
  getProductNamesForAllOrgs,
  getActiveProductsWithVersionsByOrgId,
  getProductCountsByType,
  getProductComplianceStats,
  getOrgsWithComplianceStats,
  getProductRiskActivities,
  getRisksWithActionsAndDocuments,
  generateSuggestedActivities,
  getProductsByRecursiveOrgHierachy,
  processDescriptionFromFile,
  generateAIDescription,
  getOrgNetworkData,
  clone,
  setActiveVersion,
  save,
  restoreProduct
};

async function getById(_id) {
  if (!_id) {
    return null;
  }
  return await httpService.get(`${endpoint}/${_id}`);
}

async function getByIdToArr(_id) {
  if (!_id) {
    return null;
  }
  const result = await getById(_id);
  return [result];
}

async function getAll() {
  return await httpService.get(endpoint);
}

async function getByOrgId(orgId) {
  if (!orgId) {
    return [];
  }
  return await httpService.get(`${endpoint}/org/${orgId}`);
}

/* 
  @param orgId: the orgId of one of the orgs in the generated nodes model
  @param queryOpts: optional query parameters
      @key: withOrg
      @value: 'all' or a list of fields to include in the attached org object, as a comma separted string
      @key: withUsers
      @value: 'all' or a list of fields to include in the attached activity users array, as a comma separted string
  @return a list of products for orgId and it's children orgs
*/
async function getByOrgIdWithSubs(orgId, queryOpts = {}) {
  if (!orgId) {
    return [];
  }
  let queryStr = buildQueryString(queryOpts);
  return await httpService.get(`${endpoint}/org/${orgId}/with-subs${queryStr}`);
}

async function getSummaryWithSubs(orgId, archive = false) {
  if (!orgId) {
    return [];
  }
  return await httpService.get(`${endpoint}/org/${orgId}/with-subs/summary?archive=${archive}`);
}

async function getProductNamesForAllOrgs(orgId) {
  if (!orgId) {
    return [];
  }
  return await httpService.get(`${endpoint}/org/${orgId}/all-names`);
}

async function getActiveProductsWithVersionsByOrgId(orgId) {
  if (!orgId) {
    return [];
  }
  return await httpService.get(`${endpoint}/org/${orgId}/active-versions`);
}

async function getProductCountsByType(orgId) {
  if (!orgId) {
    return {};
  }
  return await httpService.get(`${endpoint}/org/${orgId}/count-by-type`);
}

async function getProductComplianceStats(orgId) {
  if (!orgId) {
    return {};
  }
  return await httpService.get(`${endpoint}/org/${orgId}/count-by-compliance`);
}
async function getOrgsWithComplianceStats(orgId) {
  if (!orgId) {
    return {};
  }
  return await httpService.get(`${endpoint}/org/${orgId}/orgs-compliance`);
}

async function getProductRiskActivities({ productId = null, orgId = null }, type = 'actions') {
  let filterStr = productId ? `?productId=${productId}` : `?orgId=${orgId}`;
  return await httpService.get(`${endpoint}/activities/${type}${filterStr}`);
}

/* 
@deprecated use getProductRiskActivities
*/
async function getRisksWithActionsAndDocuments(productId, orgId) {
  return Promise.resolve([]);
}

async function generateSuggestedActivities(data) {
  return await httpService.put(`${endpoint}/generate-suggested-activities`, data);
}

/* 
@deprecated
*/
async function getProductsByRecursiveOrgHierachy(orgId) {
  return await httpService.get(`${endpoint}/org/hierarchy/${orgId}`);
}

async function processDescriptionFromFile(formData) {
  return await httpService.post(`${endpoint}/process-description-from-file`, formData);
}

async function generateAIDescription(data) {
  return await httpService.post(`${endpoint}/generate-ai-description`, data);
}

async function getOrgNetworkData(orgId) {
  return await httpService.get(`${endpoint}/org/${orgId}/network-data`);
}

async function clone(productId, cloneProduct) {
  return await httpService.post(`${endpoint}/clone/${productId}`, cloneProduct);
}

async function setActiveVersion(productId) {
  return await httpService.put(`${endpoint}/set-active-version/${productId}`);
}

async function save(product) {
  if (product._id) {
    return await httpService.put(`${endpoint}/${product._id}`, product);
  }
  return await httpService.post(`${endpoint}`, product);
}

async function archiveProduct(productId) {
  return await httpService.delete(`${endpoint}/${productId}`);
}

async function restoreProduct(productId) {
  return await httpService.post(`${endpoint}/${productId}/restore`);
}
